import React, { useState } from 'react';
import { UserPlus, Check, Heart, GitMerge, Radio, HelpCircle, X } from 'lucide-react';
import { T, P, BG, CARD, BORDER } from './discoverConstants';
import { formatReach } from '../../lib/format';

// Deterministic DiceBear portrait — premium, brand-safe, unlimited + free.
function dicebearFor(seed) {
  const s = encodeURIComponent((seed || 'x').replace(/[^a-zA-Z0-9]/g, ''));
  return `https://api.dicebear.com/7.x/lorelei/svg?seed=${s}&backgroundColor=0a0a0a,0f0f0f,121212&radius=12`;
}

// "Why?" affordance copy. Plain English, zero internal vocabulary
// (DESIGN_CONTRACT.md substitution table). Each badge has a one-sentence
// explanation that opens on click. Click-only, not hover, so mobile + a11y
// users get the same treatment as desktop hover.
const WHY_COPY = {
  bridge: {
    title: 'Why "Bridge"?',
    body:  "Their followers also follow creators in other groups on this list. Picking them helps your wave reach more than one audience pocket.",
  },
  multi_platform: {
    title: 'Why "Multi-platform"?',
    body:  "In past brand waves, this creator has posted the same campaign to 2+ platforms — TikTok and Instagram, or Instagram and YouTube. That means more reach per pick.",
  },
};

// Renders the explicit niche / platform pair next to the badge label so
// the chip reads "Bridge · BEAUTY + WELLNESS" instead of just "Bridge".
// Hidden when the backend couldn't derive a pair (no creator metadata).
function PairSuffix({ label }) {
  if (!label) return null;
  return (
    <span className="opacity-75 normal-case font-bold ml-0.5" style={{ letterSpacing: '0.04em' }}>
      · {label}
    </span>
  );
}

function WhyPopover({ kind, onClose }) {
  const copy = WHY_COPY[kind];
  if (!copy) return null;
  return (
    <div
      className="absolute z-20 bottom-full mb-1 left-0 right-0 mx-2 rounded-lg p-2.5 shadow-lg"
      style={{ background: '#0F0F0F', border: `1px solid ${T}40` }}
      data-testid={`why-${kind}-popover`}
      role="tooltip"
    >
      <div className="flex items-start justify-between gap-2 mb-1">
        <p className="text-[10px] font-bold text-white">{copy.title}</p>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onClose(); }}
          className="text-gray-500 hover:text-white flex-shrink-0"
          aria-label="Close explanation"
        >
          <X className="h-3 w-3" />
        </button>
      </div>
      <p className="text-[9.5px] text-gray-400 leading-relaxed">{copy.body}</p>
    </div>
  );
}

export default function CreatorCard({ creator, selected, onToggle, cmdTag }) {
  const [saved, setSaved] = useState(false);
  const [openWhy, setOpenWhy] = useState(null); // null | 'bridge' | 'multi_platform'

  const seed = creator.handle || creator.name || creator.id;
  const fallbackAvatar = dicebearFor(seed);
  const isBridge     = Boolean(cmdTag?.is_bridge);
  const isMultiPlat  = Boolean(cmdTag?.is_xplat_amplifier);

  return (
    <div
      className="rounded-xl overflow-hidden group transition-all relative"
      style={{ background: CARD, border: `1px solid ${selected ? `${T}40` : BORDER}` }}
      data-testid={`creator-card-${creator.id}`}
    >
      <div className="relative h-32 overflow-hidden" style={{ background: '#0A0A0A' }}>
        <img
          src={creator.avatar || fallbackAvatar}
          alt={creator.name}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          onError={e => { e.target.src = fallbackAvatar; }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#121212] via-transparent to-transparent" />
        <div
          className="absolute top-2 left-2 px-2 py-0.5 rounded text-[9px] font-black"
          style={{ background: `linear-gradient(135deg, ${T}, #0088AA)`, color: '#000' }}
        >
          {creator.avg_rating?.toFixed(1) || '4.5'}
        </div>
        <div className="absolute top-2 right-2 flex gap-1">
          {(creator.platforms || []).slice(0, 2).map(p => (
            <span key={p} className="px-1.5 py-0.5 rounded text-[7px] font-semibold bg-black/60 text-white capitalize">
              {p}
            </span>
          ))}
        </div>
        {/* CMD safe-surface tags (BRIDGE + MULTI-PLATFORM). Both badges
            ship with a click-to-explain "Why?" affordance so users in
            the curious tier (Tier 2) get a plain-English reason without
            having to read documentation. No quantified claims. */}
        {(isBridge || isMultiPlat) && (
          <div className="absolute bottom-2 left-2 flex gap-1" data-testid={`cmd-tags-${creator.id}`}>
            {isBridge && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); setOpenWhy(openWhy === 'bridge' ? null : 'bridge'); }}
                className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[7.5px] font-black uppercase tracking-widest transition-all hover:scale-105"
                style={{ background: 'rgba(255, 184, 77, 0.18)', color: '#FFB84D', border: '1px solid rgba(255, 184, 77, 0.45)' }}
                data-testid={`bridge-badge-${creator.id}`}
                data-niche-pair={cmdTag?.niche_pair_label || ''}
              >
                <GitMerge className="h-2.5 w-2.5" />
                Bridge
                <PairSuffix label={cmdTag?.niche_pair_label} />
                <HelpCircle className="h-2.5 w-2.5 opacity-60" />
              </button>
            )}
            {isMultiPlat && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); setOpenWhy(openWhy === 'multi_platform' ? null : 'multi_platform'); }}
                className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[7.5px] font-black uppercase tracking-widest transition-all hover:scale-105"
                style={{ background: 'rgba(0, 245, 255, 0.14)', color: T, border: `1px solid ${T}55` }}
                data-testid={`multi-platform-badge-${creator.id}`}
                data-platform-pair={cmdTag?.platform_pair_label || ''}
              >
                <Radio className="h-2.5 w-2.5" />
                Multi-platform
                <PairSuffix label={cmdTag?.platform_pair_label} />
                <HelpCircle className="h-2.5 w-2.5 opacity-60" />
              </button>
            )}
          </div>
        )}
      </div>
      <div className="p-3 space-y-2 relative">
        {/* Why-popover lives in the content area so it doesn't overflow
            the avatar; positioned above the badges via `bottom-full`. */}
        {openWhy && <WhyPopover kind={openWhy} onClose={() => setOpenWhy(null)} />}
        <div>
          <h3 className="text-[11px] font-bold text-white truncate">{creator.name}</h3>
          <p className="text-[9px] truncate" style={{ color: T }}>{creator.handle}</p>
        </div>
        <div className="flex gap-1.5 text-[8px]">
          <span className="px-1.5 py-0.5 rounded font-semibold" style={{ background: BG, color: '#fff', border: `1px solid ${BORDER}` }}>
            {formatReach(creator.followers)}
          </span>
          <span className="px-1.5 py-0.5 rounded font-semibold" style={{ background: `${T}12`, color: T, border: `1px solid ${T}25` }}>
            DNA {Math.floor(Math.random() * 15 + 85)}%
          </span>
          {creator.engagement_rate && (
            <span className="px-1.5 py-0.5 rounded font-semibold" style={{ background: `${P}12`, color: P, border: `1px solid ${P}25` }}>
              {creator.engagement_rate.toFixed(1)}% eng
            </span>
          )}
        </div>
        <div className="flex flex-wrap gap-1">
          {(creator.categories || creator.genres || []).slice(0, 2).map(cat => (
            <span key={cat} className="px-1.5 py-0.5 rounded text-[7px] text-gray-500 capitalize" style={{ background: BG, border: `1px solid ${BORDER}` }}>
              {cat}
            </span>
          ))}
        </div>
        <div className="flex gap-1.5 pt-0.5">
          <button
            onClick={() => onToggle?.(creator.id)}
            className="flex-1 py-1.5 rounded text-[9px] font-bold flex items-center justify-center gap-1 transition-all"
            style={selected ? { background: '#4ade8015', color: '#4ade80', border: `1px solid #4ade8030` } : { background: `${T}12`, color: T, border: `1px solid ${T}30` }}
            data-testid={`add-to-wave-${creator.id}`}
          >
            {selected ? <><Check className="h-3 w-3" />Added</> : <><UserPlus className="h-3 w-3" />Add to Wave</>}
          </button>
          <button
            onClick={() => setSaved(!saved)}
            className="px-2 py-1.5 rounded transition-all"
            style={saved ? { background: `${P}12`, color: P, border: `1px solid ${P}30` } : { background: BG, color: '#555', border: `1px solid ${BORDER}` }}
          >
            <Heart className={`h-3 w-3 ${saved ? 'fill-current' : ''}`} />
          </button>
        </div>
      </div>
    </div>
  );
}
